/**
 * FL-246: 複数週スケジュール表示コンポーネント
 *
 * 管理者向けの複数週（最大4週間）・複数ドライバーのスケジュール表示
 * グリッドレイアウトでドライバー別・日付別にスケジュール表示
 *
 * TDD Red-Green-Refactorサイクルに従った実装
 */

import React from 'react';
import { DriverSchedule } from '@/types/schedule';

interface Driver {
  id: string;
  name: string;
}

interface MultiWeekScheduleDisplayProps {
  /** 表示対象の週の開始日（月曜日、YYYY-MM-DD形式） */
  weekStart: string;
  /** 表示週数（1～4） */
  weekCount: number;
  /** スケジュール一覧 */
  schedules: DriverSchedule[];
  /** 表示対象ドライバー */
  drivers: Driver[];
  /** スケジュール選択時のコールバック */
  onSelectSchedule: (scheduleId: string) => void;
  /** 新規スケジュール追加時のコールバック */
  onAddSchedule: (driverId: string, date: string) => void;
}

/**
 * 指定週の営業日（月～金）を生成
 */
const generateWeekDates = (weekStart: string): string[] => {
  const dates: string[] = [];
  const baseDate = new Date(weekStart);

  for (let i = 0; i < 5; i++) {
    const date = new Date(baseDate);
    date.setDate(date.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }

  return dates;
};

/**
 * 複数週の日付を生成
 */
const generateMultiWeekDates = (weekStart: string, weekCount: number): string[][] => {
  const weeks: string[][] = [];

  for (let w = 0; w < weekCount; w++) {
    const currentWeekStart = new Date(weekStart);
    currentWeekStart.setDate(currentWeekStart.getDate() + w * 7);
    const weekStartStr = currentWeekStart.toISOString().split('T')[0];

    weeks.push(generateWeekDates(weekStartStr));
  }

  return weeks;
};

/**
 * ステータスに応じたスタイルクラスを返す
 */
const getStatusStyles = (status: DriverSchedule['status']): string => {
  switch (status) {
    case 'confirmed':
      return 'bg-green-100 dark:bg-green-900/20 border-green-400 text-green-900 dark:text-green-200';
    case 'cancelled':
      return 'bg-gray-200 dark:bg-gray-600 border-gray-400 text-gray-700 dark:text-gray-300';
    case 'registered':
    default:
      return 'bg-blue-100 dark:bg-blue-900/20 border-blue-400 text-blue-900 dark:text-blue-200';
  }
};

/**
 * 日付をフォーマット（MM/DD形式）
 */
const formatDateShort = (dateStr: string): string => {
  const date = new Date(dateStr);
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * 曜日を取得
 */
const getDayOfWeek = (dateStr: string): string => {
  const date = new Date(dateStr);
  const days = ['日', '月', '火', '水', '木', '金', '土'];
  return days[date.getDay()];
};

/**
 * MultiWeekScheduleDisplay コンポーネント
 *
 * @param weekStart - 表示対象週の開始日
 * @param weekCount - 表示週数
 * @param schedules - スケジュール一覧
 * @param drivers - ドライバー一覧
 * @param onSelectSchedule - スケジュール選択コールバック
 * @param onMoveSchedule - スケジュール移動コールバック
 * @param onAddSchedule - 新規追加コールバック
 * @returns JSX要素
 */
export const MultiWeekScheduleDisplay: React.FC<MultiWeekScheduleDisplayProps> = ({
  weekStart,
  weekCount,
  schedules,
  drivers,
  onSelectSchedule,
  onAddSchedule,
}) => {
  const weeks = generateMultiWeekDates(weekStart, weekCount);

  /**
   * 指定ドライバーの指定日付のスケジュールを取得
   */
  const getSchedulesForDateAndDriver = (date: string, driverId: string) => {
    return schedules.filter(s => s.scheduleDate === date && s.landTransporterId === driverId);
  };

  return (
    <div
      data-testid='multi-week-schedule-display'
      className='space-y-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700'
    >
      {/* タイトル */}
      <div>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          スケジュール一覧（{weekCount}週間）
        </h3>
        <p className='text-sm text-gray-600 dark:text-gray-400'>
          {formatDateShort(weeks[0][0])} ～ {formatDateShort(weeks[weekCount - 1][4])}
        </p>
      </div>

      {/* ドライバーなしの場合 */}
      {drivers.length === 0 ? (
        <div className='text-center py-8 text-gray-500 dark:text-gray-400'>
          <p>表示対象のドライバーがありません</p>
        </div>
      ) : (
        <div className='overflow-x-auto'>
          {/* グリッドレイアウト */}
          <div className='inline-block min-w-full'>
            {/* ヘッダー行（週の日付） */}
            <div className='flex gap-2'>
              {/* ドライバー名列 */}
              <div className='w-32 flex-shrink-0'>
                <div className='text-xs font-semibold text-gray-700 dark:text-gray-300 h-16 flex items-end pb-2'>
                  ドライバー
                </div>
              </div>

              {/* 各週のヘッダー */}
              {weeks.map((weekDates, weekIndex) => (
                <div
                  key={`week-${weekIndex}`}
                  className='flex gap-1 flex-shrink-0'
                  data-testid={`week-header-${weekIndex}`}
                >
                  {weekDates.map(date => (
                    <div
                      key={date}
                      className='w-20 text-center text-xs font-medium text-gray-700 dark:text-gray-300 pb-2 border-b border-gray-300 dark:border-gray-600'
                    >
                      <div>{formatDateShort(date)}</div>
                      <div className='text-gray-500 dark:text-gray-400'>({getDayOfWeek(date)})</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* ドライバー別の行 */}
            {drivers.map(driver => (
              <div
                key={driver.id}
                className='flex gap-2 border-t border-gray-200 dark:border-gray-700 py-2'
                data-testid={`driver-row-${driver.id}`}
              >
                {/* ドライバー名 */}
                <div
                  className='w-32 flex-shrink-0 px-2 font-medium text-sm text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800 z-10'
                  data-testid={`driver-name-${driver.id}`}
                >
                  <div>{driver.name}</div>
                  <div className='text-xs text-gray-500 dark:text-gray-400'>
                    {(() => {
                      const totalCount = schedules.filter(
                        s => s.landTransporterId === driver.id
                      ).length;
                      return `${totalCount}件`;
                    })()}
                  </div>
                </div>

                {/* 各週のセル */}
                {weeks.map((weekDates, weekIndex) => (
                  <div
                    key={`driver-${driver.id}-week-${weekIndex}`}
                    className='flex gap-1 flex-shrink-0'
                  >
                    {weekDates.map(date => {
                      const dateSchedules = getSchedulesForDateAndDriver(date, driver.id);

                      return (
                        <div
                          key={date}
                          className='w-20 min-h-24 border border-gray-300 dark:border-gray-600 rounded p-1 bg-gray-50 dark:bg-gray-700'
                          data-testid={`schedule-cell-${driver.id}-${date}`}
                        >
                          {dateSchedules.length > 0 ? (
                            <div className='space-y-1'>
                              {dateSchedules.map(schedule => (
                                <button
                                  key={schedule.id}
                                  onClick={() => onSelectSchedule(schedule.id)}
                                  className={`w-full text-left text-xs p-1 rounded border-l-2 cursor-pointer hover:shadow-sm transition-shadow ${getStatusStyles(schedule.status)}`}
                                  data-testid={`schedule-${schedule.id}`}
                                >
                                  <p className='font-semibold'>
                                    {schedule.timeStart} - {schedule.timeEnd}
                                  </p>
                                  {schedule.notes && (
                                    <p className='text-xs truncate'>{schedule.notes}</p>
                                  )}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <button
                              onClick={() => onAddSchedule(driver.id, date)}
                              className='w-full h-full flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded border border-dashed border-gray-300 dark:border-gray-500'
                              data-testid={`add-schedule-${driver.id}-${date}`}
                            >
                              + 追加
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 凡例 */}
      <div className='mt-6 pt-4 border-t border-gray-200 dark:border-gray-700'>
        <p className='text-xs font-medium text-gray-600 dark:text-gray-400 mb-2'>凡例</p>
        <div className='flex flex-wrap gap-4 text-xs'>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-4 bg-blue-100 dark:bg-blue-900/20 border border-blue-400 rounded'></div>
            <span className='text-gray-700 dark:text-gray-300'>登録（未確定）</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-4 bg-green-100 dark:bg-green-900/20 border border-green-400 rounded'></div>
            <span className='text-gray-700 dark:text-gray-300'>確定</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-4 bg-gray-200 dark:bg-gray-600 border border-gray-400 rounded'></div>
            <span className='text-gray-700 dark:text-gray-300'>キャンセル</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiWeekScheduleDisplay;
